import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api";

const getStatus = (ev) => {
  const now   = new Date();
  const end   = ev.endDate   ? new Date(ev.endDate)   : null;
  const start = ev.startDate ? new Date(ev.startDate) : null;
  if (end && now > end)     return "ended";
  if (start && now < start) return "upcoming";
  return "ongoing";
};

const STATUS_META = {
  ongoing:  { label: "Ongoing",  color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", accent: "#10B981" },
  upcoming: { label: "Upcoming", color: "#1D9AEC", bg: "#EFF8FF", border: "#BFDBFE", accent: "#1D9AEC" },
  ended:    { label: "Ended",    color: "#94A3B8", bg: "#F1F5F9", border: "#E2E8F0", accent: "#CBD5E1" },
};

const formatDate = (d) => d
  ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
  : "Date TBA";

function EventCard({ ev, idx, onClick }) {
  const status = getStatus(ev);
  const meta   = STATUS_META[status];
  return (
    <div
      className="card-hover"
      onClick={onClick}
      style={{
        background: "#fff", borderRadius: 12, padding: 20,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: "1px solid #E2E8F0",
        cursor: "pointer", opacity: status === "ended" ? 0.72 : 1,
        borderTop: `3px solid ${meta.accent}`,
        animation: "fadeIn 0.25s ease forwards",
        animationDelay: `${idx * 30}ms`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: `${meta.accent}15`, border: `1px solid ${meta.accent}30`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
        }}>🎟</div>
        <span style={{
          fontSize: 10, fontWeight: 700, color: meta.color,
          background: meta.bg, border: `1px solid ${meta.border}`,
          padding: "3px 9px", borderRadius: 20, letterSpacing: "0.05em", textTransform: "uppercase",
        }}>{meta.label}</span>
      </div>
      <h3 style={{ margin: "0 0 5px", fontSize: 14, fontWeight: 600, color: status === "ended" ? "#64748B" : "#0F172A", lineHeight: 1.35 }}>
        {ev.eventName}
      </h3>
      <p style={{ margin: 0, fontSize: 11.5, color: "#94A3B8" }}>
        {formatDate(ev.startDate)}{ev.endDate && ev.endDate !== ev.startDate ? ` → ${formatDate(ev.endDate)}` : ""}
      </p>
    </div>
  );
}

export default function AdminDelegatesEventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [search, setSearch] = useState("");
  const [showEnded, setShowEnded] = useState(false);

  useEffect(() => {
    api.get("/jury/delegate-events")
      .then(res => setEvents(res.data.data || []))
      .catch(() => setEvents([]));
  }, []);

  const filtered = useMemo(() => {
    if (!events) return null;
    const q = search.trim().toLowerCase();
    return q ? events.filter(ev => ev.eventName.toLowerCase().includes(q)) : events;
  }, [events, search]);

  const active   = (filtered || []).filter(ev => getStatus(ev) !== "ended");
  const ended    = (filtered || []).filter(ev => getStatus(ev) === "ended");
  const ongoing  = (filtered || []).filter(ev => getStatus(ev) === "ongoing").length;
  const upcoming = (filtered || []).filter(ev => getStatus(ev) === "upcoming").length;

  return (
    <Layout title="Delegates">
      <button
        onClick={() => navigate("/admin")}
        style={{ marginBottom: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#475569", display: "inline-flex", alignItems: "center", gap: 6, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
      >
        ← Back
      </button>

      {/* Stats + search */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
        {events && (
          <>
            {ongoing  > 0 && <span style={chip("#059669","#ECFDF5","#A7F3D0")}>● {ongoing} Ongoing</span>}
            {upcoming > 0 && <span style={chip("#1D9AEC","#EFF8FF","#BFDBFE")}>● {upcoming} Upcoming</span>}
            {ended.length > 0 && <span style={chip("#94A3B8","#F1F5F9","#E2E8F0")}>● {ended.length} Ended</span>}
          </>
        )}
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94A3B8" }}>🔍</span>
          <input
            type="text" placeholder="Search events…" value={search}
            onChange={e => { setSearch(e.target.value); if (e.target.value) setShowEnded(true); }}
            style={{ width: "100%", padding: "8px 14px 8px 34px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none", background: "#fff", fontFamily: "inherit", color: "#0F172A" }}
          />
        </div>
      </div>

      {!events ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px,1fr))", gap: 16 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <div className="skeleton" style={{ height: 14, width: "70%", marginBottom: 8 }} />
              <div className="skeleton" style={{ height: 10, width: "40%" }} />
            </div>
          ))}
        </div>
      ) : active.length === 0 && ended.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, padding: "52px 24px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🎟</div>
          <p style={{ fontWeight: 700, color: "#0F172A", margin: "0 0 4px" }}>No delegate events found</p>
          <p style={{ color: "#94A3B8", fontSize: 13, margin: 0 }}>Events with delegate registration forms will appear here.</p>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px,1fr))", gap: 16, marginBottom: ended.length > 0 ? 28 : 0 }}>
              {active.map((ev, idx) => (
                <EventCard key={ev._id} ev={ev} idx={idx} onClick={() => navigate(`/admin/delegates/${ev._id}`)} />
              ))}
            </div>
          )}

          {ended.length > 0 && (
            <div>
              <button
                onClick={() => setShowEnded(s => !s)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#64748B", padding: "4px 0", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}
              >
                <span style={{ fontSize: 16 }}>{showEnded ? "▾" : "▸"}</span>
                Ended Events ({ended.length})
              </button>
              {showEnded && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px,1fr))", gap: 16 }}>
                  {ended.map((ev, idx) => (
                    <EventCard key={ev._id} ev={ev} idx={idx} onClick={() => navigate(`/admin/delegates/${ev._id}`)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Layout>
  );
}

const chip = (color, bg, border) => ({
  fontSize: 11.5, fontWeight: 600, color,
  background: bg, border: `1px solid ${border}`,
  padding: "4px 12px", borderRadius: 20, whiteSpace: "nowrap",
});
